<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :return-value.sync="date"
    transition="scale-transition"
    offset-y
    min-width="290px"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        :hide-details="true"
        :value="dateRangeText"
        :label="$t('message.common.chooseDate')"
        prepend-icon="icon-calendar"
        readonly
        v-on="on"
        class="period-picker__input"
        :class="inputClasses"
      ></v-text-field>
    </template>
    <v-card>
      <v-radio-group
        v-model="chosenDateType"
        class="d-flex px-6 pt-6"
        @change="resetDatePicker"
      >
        <v-radio
          v-for="dateType in dateTypes"
          :key="dateType.id"
          :label="dateType.name"
          :value="dateType.value"
          class="ml-1"
        ></v-radio>
      </v-radio-group>
      <v-date-picker
        v-if="showDatepicker"
        v-model="date"
        first-day-of-week="1"
        :locale="selectedLocale"
        :type="chosenDateType"
        :allowed-dates="allowedDates"
        no-title
        scrollable
        range
        color="primary"
        class="pa-2"
        @input="updateDate"
      >
      </v-date-picker>
      <div class="px-5 py-5">
        <div class="dates-select">
          <v-text-field
            v-model="dateFrom"
            :label="$t('message.common.dateFrom')"
            :disabled="true"
          ></v-text-field
          ><v-text-field
            v-model="dateTo"
            :label="$t('message.common.dateTo')"
            :disabled="true"
          ></v-text-field>
        </div>
        <div class="d-flex justify-space-between">
          <ui-button outlined color="primary" @click="menu = false">
            {{ $t("message.common.cancel") }}
          </ui-button>
          <v-spacer />
          <ui-button
            color="primary"
            :disabled="date.length < 2"
            @click="handleDatePickerOk"
          >
            {{ $t("message.common.ok") }}
          </ui-button>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { mapState } from "vuex";

import moment from "moment";
import { PERIOD, DATEPICKER_DATE_TYPE } from "@/enums/period.enum";
import { FORMAT } from "@/config/date-format";

export default {
  name: "PeriodPicker",
  props: {
    singlePeriod: {
      type: Boolean,
      default: false
    },
    inputClasses: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      PERIOD,
      chosenDateType: DATEPICKER_DATE_TYPE.DATE,
      dateTypes: [
        {
          id: PERIOD.WEEK,
          name: this.$t("message.common.week"),
          value: DATEPICKER_DATE_TYPE.DATE
        },
        {
          id: PERIOD.MONTH,
          name: this.$t("message.common.month"),
          value: DATEPICKER_DATE_TYPE.MONTH
        }
      ],
      date: [
        this.getWeekPeriodStartByDate(moment()),
        this.getWeekPeriodEndByDate(moment())
      ],
      dateFrom: this.getWeekPeriodStartByDate(moment()),
      dateTo: this.getWeekPeriodEndByDate(moment()),
      menu: false,
      showDatepicker: true
    };
  },

  computed: {
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    }),
    isWeekType() {
      return this.chosenDateType === DATEPICKER_DATE_TYPE.DATE;
    },
    dateRangeText() {
      if (this.date.length > 1) {
        return this.date.join(" ~ ");
      }
      return this.date[0];
    }
  },

  created() {
    this.$emit("update", {
      date: this.formatDateWithDays(this.date),
      type: this.mapDateTypeToBackendEnum(this.chosenDateType)
    });
  },

  methods: {
    updateDate(newDate) {
      if (this.singlePeriod && newDate && newDate.length === 1) {
        // if single period mode, update date after start date chosen
        if (this.isWeekType) {
          this.dateFrom = moment(newDate[0])
            .startOf(PERIOD.ISO_WEEK)
            .format(FORMAT.DATE);
          this.dateTo = moment(newDate[0])
            .endOf(PERIOD.ISO_WEEK)
            .format(FORMAT.DATE);
        } else {
          this.dateFrom = moment(newDate[0]).format(FORMAT.MONTH);
          this.dateTo = moment(newDate[0]).format(FORMAT.MONTH);
        }
        this.date = [this.dateFrom, this.dateTo];
      } else if (newDate && newDate.length === 2) {
        // update dates only if range chosen (start and end date)
        const startDate = moment(newDate[0]);
        const endDate = moment(newDate[1]);
        const updatedDate = startDate.isBefore(endDate)
          ? [...newDate]
          : [newDate[1], newDate[0]];

        if (updatedDate[0] && this.dateFrom !== updatedDate[0]) {
          this.dateFrom = updatedDate[0];
        }
        if (updatedDate[1] && this.dateTo !== updatedDate[1]) {
          this.dateTo = updatedDate[1];
        }

        if (this.isWeekType) {
          // set dates to beginning and end of week
          this.dateFrom = moment(this.dateFrom)
            .startOf(PERIOD.ISO_WEEK)
            .format(FORMAT.DATE);
          this.dateTo = moment(this.dateTo)
            .endOf(PERIOD.ISO_WEEK)
            .format(FORMAT.DATE);
        }
        this.date = [this.dateFrom, this.dateTo];
      }
    },

    allowedDates(val) {
      return moment(val).isBefore(
        this.chosenDateType === DATEPICKER_DATE_TYPE.DATE
          ? moment().startOf(PERIOD.ISO_WEEK)
          : moment()
              .subtract(1, PERIOD.MONTH)
              .endOf(PERIOD.MONTH)
      );
    },

    mapDateTypeToBackendEnum(type) {
      switch (type) {
        case DATEPICKER_DATE_TYPE.DATE:
          return 1;
        case DATEPICKER_DATE_TYPE.MONTH:
          return 2;
        default:
          return null;
      }
    },

    formatDateWithDays(date) {
      return date.length === 2
        ? [
            moment(date[0]).format(FORMAT.DATE),
            moment(date[1]).format(FORMAT.DATE)
          ]
        : [];
    },

    getWeekPeriodStartByDate(date) {
      return date
        .subtract(this.singlePeriod ? 1 : 8, PERIOD.WEEK)
        .startOf(PERIOD.ISO_WEEK)
        .format(FORMAT.DATE);
    },
    getWeekPeriodEndByDate(date) {
      return date
        .subtract(1, PERIOD.WEEK)
        .endOf(PERIOD.ISO_WEEK)
        .format(FORMAT.DATE);
    },

    getMonthPeriodStartByDate(date) {
      return date
        .subtract(this.singlePeriod ? 0 : 4, PERIOD.MONTH)
        .format(FORMAT.MONTH);
    },
    getMonthPeriodEndByDate(date) {
      return date.format(FORMAT.MONTH);
    },

    updateDateByRouteChange() {
      // method used from parent component
      this.dateFrom = this.isWeekType
        ? this.getWeekPeriodStartByDate(moment(this.dateTo).add(1, PERIOD.WEEK))
        : this.getMonthPeriodStartByDate(moment(this.dateTo));

      this.dateTo = this.isWeekType
        ? this.getWeekPeriodEndByDate(moment(this.dateTo).add(1, PERIOD.WEEK))
        : this.getMonthPeriodEndByDate(moment(this.dateTo));

      this.date = [this.dateFrom, this.dateTo];

      this.$emit("update", {
        date: this.formatDateWithDays(this.date),
        type: this.mapDateTypeToBackendEnum(this.chosenDateType)
      });
    },

    handleDatePickerOk() {
      if (this.date.length === 1) {
        this.date = [this.date[0], this.date[0]];
      }
      this.$refs.menu.save(this.date);
      this.$emit("update", {
        date: this.formatDateWithDays(this.date),
        type: this.mapDateTypeToBackendEnum(this.chosenDateType)
      });
    },

    resetDatePicker() {
      this.showDatepicker = false;
      this.$nextTick(() => {
        this.showDatepicker = true;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.period-picker {
  &__input {
    min-width: 240px;
  }
}

.dates-select {
  display: flex;
  align-items: center;
  padding: $spacing 0;
  border-top: 1px solid $light-3;
  margin-bottom: $spacing;

  .v-text-field {
    width: 100px;
    margin-bottom: -$spacing * 2;
  }

  & > :not(:last-child) {
    margin-right: $spacing;
  }
}

::v-deep.v-input--radio-group--column .v-input--radio-group__input {
  flex-direction: row;
  align-items: center;
}

::v-deep.v-input--radio-group--column
  .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 0;
}
::v-deep.v-input--radio-group--column .v-radio {
  width: 50%;
}
</style>
